import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import Icon from '../../../../components/AppIcon';
import { SCENARIO_COLORS } from './analysisMetrics';
import { mergeProjectionsByYear, formatYAxis } from './analysisCompareUtils';
import ScenarioDeltaBadge from './ScenarioDeltaBadge';
import ScenarioCompareTable from './ScenarioCompareTable';
import CompareCashflowTooltip from './CompareCashflowTooltip';

const BAR_METRICS = [
  { id: 'netWorth', label: 'Valeur nette', keyA: 'netWorthA', keyB: 'netWorthB', higherIsBetter: true },
  { id: 'cashflow', label: 'Impact cashflow (actifs)', keyA: 'cashflowA', keyB: 'cashflowB', higherIsBetter: true },
  { id: 'revenue', label: 'Revenus bruts', keyA: 'revenueA', keyB: 'revenueB', higherIsBetter: true },
  { id: 'tax', label: 'Impôt annuel', keyA: 'taxA', keyB: 'taxB', higherIsBetter: false },
];

const ScenarioComparePanel = ({
  projectionA,
  projectionB,
  labelA,
  labelB,
  visibleMetrics,
  yearIndex,
  formatCurrency,
}) => {
  const rowsA = projectionA?.data || [];
  const rowsB = projectionB?.data || [];
  const merged = useMemo(() => mergeProjectionsByYear(rowsA, rowsB), [rowsA, rowsB]);
  const rowA = rowsA[yearIndex] || rowsA[rowsA.length - 1] || {};
  const rowB = rowsB[yearIndex] || rowsB[rowsB.length - 1] || {};
  const lastA = rowsA[rowsA.length - 1] || {};
  const lastB = rowsB[rowsB.length - 1] || {};
  const ctxA = { firstRow: rowsA[0], lastRow: lastA };
  const ctxB = { firstRow: rowsB[0], lastRow: lastB };

  const barAtYear = useMemo(() => {
    const m = merged[yearIndex] || merged[merged.length - 1];
    if (!m) return [];
    return BAR_METRICS.map((bm) => ({
      name: bm.label,
      a: m[bm.keyA] ?? 0,
      b: m[bm.keyB] ?? 0,
      higherIsBetter: bm.higherIsBetter,
    }));
  }, [merged, yearIndex]);

  const horizonGap = (lastB.netWorth || 0) - (lastA.netWorth || 0);
  const gapTone = horizonGap >= 0 ? 'positive' : 'negative';

  return (
    <div className="space-y-6" data-testid="scenario-compare-panel">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
        <div
          className="rounded-xl border-2 bg-card p-4"
          style={{ borderColor: SCENARIO_COLORS.A, borderLeftWidth: 6 }}
        >
          <p className="text-xs text-muted-foreground mb-1">Scénario A</p>
          <p className="font-semibold text-foreground mb-2" style={{ color: SCENARIO_COLORS.A }}>{labelA}</p>
          <p className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-data)' }}>
            {formatCurrency(rowA.netWorth || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Valeur nette — année {rowA.year}</p>
        </div>

        <div className="flex flex-col items-center justify-center gap-2 py-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Écart</span>
          <ScenarioDeltaBadge
            valueA={rowA.netWorth || 0}
            valueB={rowB.netWorth || 0}
            higherIsBetter
            format="currency"
            formatCurrency={formatCurrency}
            size="md"
          />
        </div>

        <div
          className="rounded-xl border-2 bg-card p-4"
          style={{ borderColor: SCENARIO_COLORS.B, borderLeftWidth: 6 }}
        >
          <p className="text-xs text-muted-foreground mb-1">Scénario B</p>
          <p className="font-semibold text-foreground mb-2" style={{ color: SCENARIO_COLORS.B }}>{labelB}</p>
          <p className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-data)' }}>
            {formatCurrency(rowB.netWorth || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Valeur nette — année {rowB.year}</p>
        </div>
      </div>

      <ScenarioCompareTable
        metrics={visibleMetrics}
        rowA={rowA}
        rowB={rowB}
        bundleA={projectionA}
        bundleB={projectionB}
        ctxA={ctxA}
        ctxB={ctxB}
        labelA={labelA}
        labelB={labelB}
        formatCurrency={formatCurrency}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Icon name="TrendingUp" size={16} style={{ color: SCENARIO_COLORS.A }} />
            Valeur nette comparée
          </h4>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatYAxis} width={52} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
                <Legend wrapperStyle={{ fontSize: '0.65rem' }} />
                <Line dataKey="netWorthA" name={labelA} stroke={SCENARIO_COLORS.A} strokeWidth={2.5} dot={false} />
                <Line dataKey="netWorthB" name={labelB} stroke={SCENARIO_COLORS.B} strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Icon name="Activity" size={16} style={{ color: SCENARIO_COLORS.B }} />
            Impact cashflow comparé (somme par actif)
          </h4>
          <p className="text-[0.65rem] text-muted-foreground mb-2">
            Même total que le tableau Ventilation cashflow (Perso + Inc.). Si les totaux du tableau sont identiques, les deux courbes se superposent.
          </p>
          <div className="h-[260px]" data-testid="compare-cashflow-impact-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={formatYAxis} width={52} tick={{ fontSize: 10 }} />
                <Tooltip
                  content={(
                    <CompareCashflowTooltip
                      labelA={labelA}
                      labelB={labelB}
                      formatCurrency={formatCurrency}
                    />
                  )}
                />
                <Legend wrapperStyle={{ fontSize: '0.65rem' }} />
                <Line dataKey="cashflowA" name={labelA} stroke={SCENARIO_COLORS.A} strokeWidth={2} dot={false} />
                <Line dataKey="cashflowB" name={labelB} stroke={SCENARIO_COLORS.B} strokeWidth={2} dot={false} strokeDasharray="6 3" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h4 className="text-sm font-semibold mb-3">Indicateurs clés — année sélectionnée</h4>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barAtYear}
              layout="vertical"
              margin={{ top: 4, right: 16, left: 80, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis type="number" tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={76} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
              <Legend wrapperStyle={{ fontSize: '0.65rem' }} />
              <Bar dataKey="a" name={labelA} fill={SCENARIO_COLORS.A} radius={[0, 4, 4, 0]} />
              <Bar dataKey="b" name={labelB} fill={SCENARIO_COLORS.B} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {rowsA.length > 0 && rowsB.length > 0 && (
        <div
          className={`p-4 rounded-xl border ${gapTone === 'positive' ? 'bg-[var(--color-success)]/10 border-[var(--color-success)]/30' : 'bg-[var(--color-destructive)]/10 border-[var(--color-destructive)]/30'}`}
          data-testid="comparison-highlight"
        >
          <p className="text-sm text-foreground">
            À l&apos;horizon, <span className="font-semibold" style={{ color: SCENARIO_COLORS.B }}>{labelB}</span>
            {' '}
            {horizonGap >= 0 ? 'dépasse' : 'est sous'}
            {' '}
            <span className="font-semibold" style={{ color: SCENARIO_COLORS.A }}>{labelA}</span>
            {' '}de{' '}
            <span className={`font-bold ${gapTone === 'positive' ? 'text-[var(--color-success)]' : 'text-[var(--color-destructive)]'}`}>
              {formatCurrency(Math.abs(horizonGap))}
            </span>
            {' '}en valeur nette.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScenarioComparePanel;
